/**
 * ResponsiveLayout Component - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, LogOut } from 'lucide-react';

const ResponsiveLayout = ({ 
  children, 
  navItems = [], 
  userRole = 'principal',
  userName = 'User',
  onLogout
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location, isMobile]);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 flex font-body" data-testid={`${userRole}-layout`}>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-sanjaya-navy px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          <div className="text-center">
            <h1 className="text-lg font-heading font-bold text-white">
              सञ्जय
            </h1>
            <p className="text-xs text-sanjaya-orange font-semibold">SANJAYA</p>
          </div>
          
          <div className="w-11 h-11 rounded-full bg-sanjaya-orange flex items-center justify-center text-sanjaya-navy font-bold text-lg">
            {userName?.charAt(0) || 'U'}
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <nav 
        className={`
          fixed lg:relative top-0 left-0 h-screen z-50
          w-72 lg:w-64 bg-sanjaya-navy
          transform transition-transform duration-300 ease-in-out
          ${isMobile ? (isSidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
          flex flex-col overflow-y-auto flex-shrink-0
        `}
        data-testid={`${userRole}-sidebar`}
      >
        {/* Desktop Logo */}
        <div className="hidden lg:flex items-center gap-3 p-6 border-b border-white/10">
          <div className="w-12 h-12 rounded-xl bg-sanjaya-orange flex items-center justify-center">
            <span className="text-sanjaya-navy font-heading font-bold text-xl">स</span>
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl text-white">सञ्जय</h1>
            <p className="text-xs text-sanjaya-orange font-semibold tracking-wide">SANJAYA</p>
          </div>
        </div>

        {/* Mobile Header Spacer */}
        <div className="lg:hidden h-4" />

        {/* User Info Card */}
        <div className="p-4 mx-4 mt-4 lg:mt-2 bg-white/10 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-sanjaya-orange flex items-center justify-center text-sanjaya-navy font-bold text-lg">
              {userName?.charAt(0) || 'U'}
            </div>
            <div>
              <p className="font-bold text-white text-lg">{userName}</p>
              <p className="text-sm text-sanjaya-orange capitalize font-medium">{userRole}</p>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 p-4 space-y-1">
          <p className="text-xs font-bold text-white/50 uppercase tracking-wider px-3 mb-3">
            Menu
          </p>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-semibold
                transition-all duration-200
                ${isActive(item.path) 
                  ? 'bg-sanjaya-orange text-sanjaya-navy' 
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
                }
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.icon && <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'text-sanjaya-navy' : 'text-white/60'}`} />}
              <span className="flex-1">{item.label}</span>
              {isActive(item.path) && <ChevronRight className="w-5 h-5 text-sanjaya-navy" />}
            </Link>
          ))}
        </div>

        {/* Logout Button */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl bg-white/10 text-white font-semibold text-base hover:bg-white/20 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-20 lg:pt-0 min-w-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ResponsiveLayout;
